import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { pmApi } from '../../api/pm.js';
import { jiraApi } from '../../api/jira.js';
import { useRealtime } from '../../hooks/useRealtime.js';
import MentionInput from '../../components/MentionInput.jsx';
import ActivityFeed from '../../components/ActivityFeed.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import DiscussThread from '../../plus/messenger/DiscussThread.jsx';
import MentionText from '../../components/MentionText.jsx';
import UserPill, { TagPill } from '../../components/UserPill.jsx';
import { contrastText } from '../../utils/contrast.js';
import { pushRecentItem } from '../../lib/recentItems.js';

export default function IssueDetail() {
  const { key } = useParams();
  const _nav = useNavigate();
  const { can } = useAuth();
  // Track recently visited issues so the command palette can offer them.
  useEffect(() => { if (key) pushRecentItem({ key, title: key, url: `/pm/issues/${key}` }); }, [key]);
  const [issue, setIssue] = useState(null);
  const [comments, setComments] = useState([]);
  const [worklogs, setWorklogs] = useState([]);
  const [deps, setDeps] = useState({ outgoing: [], incoming: [] });
  const [traceability, setTraceability] = useState(null);
  const [tab, setTab] = useState('comments');
  const [error, setError] = useState(null);
  const [timer, setTimer] = useState(null); // { startedAt }

  const reload = async () => {
    const r = await pmApi.getIssueByKey(key);
    setIssue(r?.issue);
    if (r?.issue?.id) {
      const id = r.issue.id;
      const [c, w, d, t] = await Promise.all([
        pmApi.comments(id),
        pmApi.worklogs(id),
        pmApi.dependencies(id),
        pmApi.traceability(id),
      ]);
      setComments(c?.items || []);
      setWorklogs(w?.items || []);
      setDeps({ outgoing: d?.outgoing || [], incoming: d?.incoming || [] });
      setTraceability(t);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload().catch(() => {}); /* eslint-disable-next-line */ }, [key]);

  // Live updates: refresh on incoming comments and status changes from the room.
  useRealtime({
    type: 'issue',
    id: issue?.id,
    events: {
      'issue:comment:new': () => { reload().catch(() => {}); },
      'issue:status:updated': () => { reload().catch(() => {}); },
    },
  });

  if (!issue) return <div className="muted">Loading…</div>;

  return (
    <section
      className="issue-detail"
      data-testid="issue-detail"
      data-loaded={issue ? 'true' : 'false'}
      data-issue-key={issue.full_key}
    >
      <header className="page-header">
        <div>
          <p className="muted small">
            <Link to="/pm/projects">Projects</Link> ›{' '}
            <Link to={`/pm/projects/${issue.project_key}/board`}>{issue.project_name}</Link>
          </p>
          <h1><code>{issue.full_key}</code> {issue.title}</h1>
          <div className="chips">
            <span className={`issue-type t-${issue.type}`}>{issue.type}</span>
            <span className={`prio-dot prio-${issue.priority}`} title={issue.priority} />
            <span className="muted small">{issue.priority}</span>
            <span className="chip">{issue.status}</span>
            {issue.story_points != null && <span className="chip">{Number(issue.story_points)} pts</span>}
            {issue.epic_title && <span className="chip" style={{ background: issue.epic_color || '#e0e7ff', color: contrastText(issue.epic_color || '#e0e7ff') }}>Epic: {issue.epic_title}</span>}
          </div>
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}

      <div className="grid-2">
        <div>
          <DescriptionEditor issue={issue} onUpdated={reload} setError={setError} />

          <div className="card">
            <div className="tabs">
              <button className={tab === 'comments' ? 'tab active' : 'tab'} onClick={() => setTab('comments')}>Comments ({comments.length})</button>
              <button className={tab === 'worklog' ? 'tab active' : 'tab'} onClick={() => setTab('worklog')}>Worklog ({worklogs.length})</button>
              <button className={tab === 'history' ? 'tab active' : 'tab'} onClick={() => setTab('history')}>History</button>
              <button className={tab === 'links' ? 'tab active' : 'tab'} onClick={() => setTab('links')}>Links</button>
              <button className={tab === 'trace' ? 'tab active' : 'tab'} onClick={() => setTab('trace')}>Traceability</button>
            </div>
            {tab === 'comments' && <Comments comments={comments} issue={issue} reload={reload} />}
            {tab === 'worklog' && <Worklog worklogs={worklogs} issue={issue} reload={reload} timer={timer} setTimer={setTimer} />}
            {tab === 'history' && <History issueId={issue.id} />}
            {tab === 'links' && <Links issueId={issue.id} />}
            {tab === 'trace' && <Trace data={traceability} />}
          </div>
        </div>

        <aside>
          <Sidebar issue={issue} reload={reload} setError={setError} deps={deps} />
          <div className="card">
            <ActivityFeed resourceType="issue" resourceId={issue.id} title="Activity" />
          </div>
          {can('messenger_thread', 'read') && (
            <div className="card" data-testid="issue-discussion">
              <h3>Discussion</h3>
              <DiscussThread
                subjectType="issue"
                subjectId={issue.id}
                subjectTitle={`${issue.full_key || ''} ${issue.title || ''}`.trim()}
                shareUrl={`/pm/issues/${issue.full_key}`}
              />
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

// Description is now rendered via the TipTap-based <IssueDescription>.
// We import it inside the component file (top of file) to keep the Tiptap
// editor lazy-bundled with the issue detail page.
import IssueDescription from '../../components/issues/IssueDescription.jsx';

function DescriptionEditor({ issue }) {
  return (
    <div className="card">
      <header className="page-header"><h3>Description</h3></header>
      <IssueDescription issueId={issue.id} initialHtml={issue.description || ''} />
    </div>
  );
}

function Comments({ comments, issue, reload }) {
  const [body, setBody] = useState('');
  async function add(e) {
    e.preventDefault();
    if (!body.trim()) return;
    // Server parses mentions from the body, so we don't need to send IDs here.
    await pmApi.addComment(issue.id, body, []);
    setBody('');
    reload();
  }
  return (
    <>
      <ul className="comment-list" data-testid="issue-comments">
        {comments.map((c) => (
          <li key={c.id} data-testid="comment-item" data-comment-id={c.id}>
            <header>
              <strong>{c.user_name || c.user_email}</strong>
              <span className="muted small">{new Date(c.created_at).toLocaleString()}</span>
            </header>
            <p data-testid="comment-body"><MentionText body={c.body} /></p>
          </li>
        ))}
        {comments.length === 0 && <li className="muted">No comments yet.</li>}
      </ul>
      <form onSubmit={add} className="comment-form" data-testid="comment-form">
        {/* MentionInput renders the actual <textarea> but does not forward
            arbitrary DOM props, so the test hook lives on this wrapper.
            `[data-testid="comment-input"] textarea` then resolves the field. */}
        <div data-testid="comment-input">
          <MentionInput
            value={body}
            onChange={setBody}
            placeholder="Add a comment… (type @ to mention)"
            rows={3}
          />
        </div>
        <div className="actions">
          <button className="btn primary" type="submit" data-testid="comment-submit">Comment</button>
        </div>
      </form>
    </>
  );
}

function Worklog({ worklogs, issue, reload, timer, setTimer }) {
  const [hours, setHours] = useState('1.0');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState('');
  const total = useMemo(() => worklogs.reduce((s, w) => s + Number(w.hours), 0), [worklogs]);

  async function add(e) {
    e.preventDefault();
    await pmApi.addWorklog(issue.id, { hours: Number(hours), logDate: date, description: desc });
    setDesc('');
    reload();
  }

  function startTimer() { setTimer({ startedAt: Date.now() }); }
  async function stopTimer() {
    if (!timer) return;
    const elapsed = (Date.now() - timer.startedAt) / 3600000;
    setTimer(null);
    await pmApi.addWorklog(issue.id, {
      hours: Math.round(elapsed * 100) / 100,
      logDate: new Date().toISOString().slice(0, 10),
      description: 'Logged via timer',
    });
    reload();
  }

  return (
    <>
      <p className="muted">Total logged: <strong>{total.toFixed(2)}h</strong></p>
      <div className="actions" style={{ marginBottom: 12 }}>
        {!timer ? <button className="btn ghost small" onClick={startTimer}>▶ Start timer</button> :
          <button className="btn primary small" onClick={stopTimer}>■ Stop & log</button>}
      </div>
      <ul className="worklog-list">
        {worklogs.map((w) => (
          <li key={w.id}>
            <strong>{Number(w.hours).toFixed(2)}h</strong> · {w.log_date} · <span className="muted">{w.user_name}</span>
            {w.description && <p className="muted small">{w.description}</p>}
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="row-form" style={{ marginTop: 12 }}>
        <input type="number" step="0.25" min="0.25" value={hours} onChange={(e) => setHours(e.target.value)} required />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What did you work on?" />
        <button type="submit" className="btn primary">Log time</button>
      </form>
    </>
  );
}

function Trace({ data }) {
  if (!data) return <div className="muted">Loading…</div>;
  return (
    <div className="trace">
      <div className="trace-summary chips">
        <span className="chip">Commits: {data.summary.commits}</span>
        <span className="chip status-pending">Tests: {data.summary.tests}</span>
        <span className="chip status-approved">Pass: {data.summary.passingTests}</span>
        <span className="chip status-rejected">Fail: {data.summary.failingTests}</span>
        <span className="chip">Defects: {data.summary.defects}</span>
        <span className="chip">Releases: {data.summary.releasedIn.join(', ') || '—'}</span>
      </div>

      <h4>Commits</h4>
      <ul className="trace-list">
        {data.commits.map((c) => (
          <li key={c.id}><code>{c.sha.slice(0, 7)}</code> {c.message?.slice(0, 80)} <span className="muted small">{c.author || '—'}</span></li>
        ))}
        {data.commits.length === 0 && <li className="muted">No commits linked.</li>}
      </ul>

      <h4>Test cases ({data.testCases.length})</h4>
      <ul className="trace-list">
        {data.testCases.map((tc) => (
          <li key={tc.id}>{tc.title} <span className="muted small">{tc.suite_name}</span></li>
        ))}
        {data.testCases.length === 0 && <li className="muted">No test cases linked.</li>}
      </ul>

      <h4>Latest executions</h4>
      <ul className="trace-list">
        {data.executions.slice(0, 10).map((e) => (
          <li key={e.id}>
            <span className={`chip status-${e.status === 'pass' ? 'approved' : e.status === 'fail' ? 'rejected' : 'pending'}`}>{e.status}</span>
            <span className="muted small">{e.plan_name} · {new Date(e.executed_at).toLocaleString()}</span>
          </li>
        ))}
      </ul>

      <h4>Releases</h4>
      <ul className="trace-list">
        {data.releases.map((r) => <li key={r.id}><code>{r.version}</code> {r.name} <span className={`chip status-${r.status}`}>{r.status}</span></li>)}
        {data.releases.length === 0 && <li className="muted">Not yet in any release.</li>}
      </ul>
    </div>
  );
}

function Sidebar({ issue, reload, setError, deps }) {
  const [watchers, setWatchers] = useState([]);
  const [voteState, setVoteState] = useState({ count: issue.vote_count || 0, voted: false });
  const [components, setComponents] = useState([]);
  const [versions, setVersions] = useState([]);
  const [labels, setLabels] = useState([]);
  const [issueLabels, setIssueLabels] = useState([]);
  const [creator, setCreator] = useState(null);

  useEffect(() => {
    jiraApi.watchers(issue.id).then((d) => setWatchers(d?.items || []));
    jiraApi.issueLabels(issue.id).then((d) => setIssueLabels(d?.items || []));
    if (issue.project_id) {
      jiraApi.components(issue.project_id).then((d) => setComponents(d?.items || []));
      jiraApi.versions(issue.project_id).then((d) => setVersions(d?.items || []));
      jiraApi.labels(issue.project_id).then((d) => setLabels(d?.items || []));
    }
    // Resolve creator info for the supervisor snapshot panel.
    if (issue.created_by_supervisor_id || issue.reporter_id) {
      const _ids = [issue.reporter_id, issue.created_by_supervisor_id].filter(Boolean);
      const url = (import.meta.env.VITE_API_BASE_URL || '/api') + '/users';
      fetch(`${url}?limit=200`, { headers: { Authorization: `Bearer ${localStorage.getItem('bos.access')}` } })
        .then((r) => r.json())
        .then((j) => {
          const all = j?.data?.items || [];
          setCreator({
            reporter: all.find((u) => u.id === issue.reporter_id) || { name: issue.reporter_name },
            supervisor: all.find((u) => u.id === issue.created_by_supervisor_id),
          });
        }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.id, issue.project_id, issue.reporter_id, issue.created_by_supervisor_id]);

  async function patch(field, value) {
    try { await pmApi.updateIssue(issue.id, { [field]: value }); reload(); }
    catch (err) { setError(err.response?.data?.message || 'Failed'); }
  }

  async function toggleWatch() {
    try {
      const list = watchers.some((w) => w.id === issue.reporter_id) ? watchers : watchers;
      // Simple approach — call watch every time; backend is idempotent.
      const fn = list.some((w) => w.id === window?.__userId) ? jiraApi.unwatch : jiraApi.watch;
      const r = await fn(issue.id);
      setWatchers(r?.items || []);
    } catch {}
  }
  async function toggleVote() {
    try {
      const r = voteState.voted ? await jiraApi.unvote(issue.id) : await jiraApi.vote(issue.id);
      setVoteState({ count: r.vote_count, voted: r.voted });
    } catch {}
  }
  async function toggleLabel(labelId) {
    const next = issueLabels.some((l) => l.id === labelId)
      ? issueLabels.filter((l) => l.id !== labelId).map((l) => l.id)
      : [...issueLabels.map((l) => l.id), labelId];
    const r = await jiraApi.setIssueLabels(issue.id, next);
    setIssueLabels(r?.items || []);
  }

  return (
    <div className="card">
      <div className="actions" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <button className={`btn ghost small ${voteState.voted ? 'active' : ''}`} onClick={toggleVote}>★ Vote ({voteState.count})</button>
        <button className="btn ghost small" onClick={toggleWatch}>👁 Watch ({watchers.length})</button>
      </div>

      <h3>Details</h3>
      <dl className="kv">
        <dt>Status</dt>
        <dd>
          <select aria-label="Status" value={issue.status} onChange={(e) => patch('status', e.target.value)}>
            <option>todo</option><option>in_progress</option><option>in_review</option><option>done</option>
          </select>
        </dd>
        <dt>Priority</dt>
        <dd>
          <select aria-label="Priority" value={issue.priority} onChange={(e) => patch('priority', e.target.value)}>
            <option>critical</option><option>high</option><option>medium</option><option>low</option>
          </select>
        </dd>
        <dt>Type</dt>
        <dd>
          <select aria-label="Type" value={issue.type} onChange={(e) => patch('type', e.target.value)}>
            <option>story</option><option>task</option><option>bug</option><option>improvement</option><option>spike</option><option>subtask</option>
          </select>
        </dd>
        <dt>Story points</dt>
        <dd><input aria-label="Story points" type="number" step="0.5" defaultValue={issue.story_points || ''} onBlur={(e) => patch('storyPoints', e.target.value === '' ? null : Number(e.target.value))} style={{ width: 80 }} /></dd>
        <dt>Due date</dt>
        <dd><input aria-label="Due date" type="date" defaultValue={issue.due_date || ''} onBlur={(e) => patch('dueDate', e.target.value || null)} /></dd>
        <dt>Reporter</dt>
        <dd className="muted">{issue.reporter_name || '—'}</dd>
        <dt>Assignee</dt>
        <dd className="muted">{issue.assignee_name || 'Unassigned'}</dd>
      </dl>

      <h3>Component / Version</h3>
      <dl className="kv">
        <dt>Component</dt>
        <dd>
          <select aria-label="Component" value={issue.component_id || ''} onChange={(e) => patch('componentId', e.target.value || null)}>
            <option value="">— none —</option>
            {components.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </dd>
        <dt>Version</dt>
        <dd>
          <select aria-label="Version" value={issue.version_id || ''} onChange={(e) => patch('versionId', e.target.value || null)}>
            <option value="">— unscheduled —</option>
            {versions.map((v) => <option key={v.id} value={v.id}>{v.name} {v.status === 'released' ? '✓' : ''}</option>)}
          </select>
        </dd>
      </dl>

      <h3>Labels</h3>
      <div className="chips">
        {labels.map((l) => {
          const selected = issueLabels.some((il) => il.id === l.id);
          // Indicate unselected with an outline (not opacity, which washes out
          // both background and text and fails WCAG contrast).
          const style = selected
            ? { background: l.color, color: contrastText(l.color), cursor: 'pointer', border: `1px solid ${l.color}` }
            : { background: 'transparent', color: '#0f172a', cursor: 'pointer', border: `1px solid ${l.color}` };
          return (
            <button
              key={l.id}
              type="button"
              className="chip"
              style={{ ...style, font: 'inherit' }}
              aria-pressed={selected}
              onClick={() => toggleLabel(l.id)}
            >{l.name}</button>
          );
        })}
        {labels.length === 0 && <span className="muted small">No labels in this project.</span>}
      </div>

      {/* PART A — Supervisor snapshot from creation time */}
      {(creator?.reporter || creator?.supervisor || (issue.created_by_tags || []).length) && (
        <>
          <h3>Created by</h3>
          <dl className="kv">
            <dt>Reporter</dt>
            <dd>{creator?.reporter ? <UserPill user={creator.reporter} /> : <span className="muted">{issue.reporter_name || '—'}</span>}</dd>
            {creator?.supervisor && (<><dt>Supervised by</dt><dd><UserPill user={creator.supervisor} /></dd></>)}
            {(issue.created_by_tags || []).length > 0 && (
              <>
                <dt>Tags at creation</dt>
                <dd className="chips">{issue.created_by_tags.map((s) => <TagPill key={s} slug={s} />)}</dd>
              </>
            )}
          </dl>
        </>
      )}

      <h3>Watchers ({watchers.length})</h3>
      <div className="chips">
        {watchers.map((w) => <span key={w.id} className="user-pill sm" title={w.email}><span className="up-avatar">{(w.name || '?').charAt(0).toUpperCase()}</span></span>)}
        {watchers.length === 0 && <span className="muted small">No watchers.</span>}
      </div>

      <h3>Dependencies</h3>
      {deps.outgoing.length === 0 && deps.incoming.length === 0 && <div className="muted small">No dependencies.</div>}
      <ul className="dep-list">
        {deps.outgoing.map((d) => (
          <li key={d.dep_id}>
            <span className="chip">{d.type.replace(/_/g, ' ')}</span>
            <Link to={`/pm/issues/${d.project_key}-${d.issue_no}`}><code>{d.project_key}-{d.issue_no}</code> {d.title}</Link>
          </li>
        ))}
        {deps.incoming.map((d) => (
          <li key={d.dep_id} className="incoming">
            <Link to={`/pm/issues/${d.project_key}-${d.issue_no}`}><code>{d.project_key}-{d.issue_no}</code> {d.title}</Link>
            <span className="chip muted">↑ {d.type.replace(/_/g, ' ')}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function History({ issueId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { jiraApi.history(issueId).then((d) => setItems(d?.items || [])); }, [issueId]);
  if (items.length === 0) return <div className="muted">No history yet.</div>;
  return (
    <ul className="comment-list">
      {items.map((h) => (
        <li key={h.id}>
          <header>
            <strong>{h.changed_by_name || 'Someone'}</strong>
            <span className="muted small">{new Date(h.changed_at).toLocaleString()}</span>
          </header>
          <p className="muted small">
            changed <strong>{h.field_name}</strong> from <code>{h.old_value || '∅'}</code> to <code>{h.new_value || '∅'}</code>
          </p>
        </li>
      ))}
    </ul>
  );
}

function Links({ issueId }) {
  const [linkTypes, setLinkTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState(null);

  function reload() { jiraApi.links(issueId).then((d) => setItems(d?.items || [])); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { jiraApi.linkTypes().then((d) => setLinkTypes(d?.items || [])); reload(); }, [issueId]);

  async function add(e) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const targetKey = fd.get('targetKey');
    const linkTypeId = fd.get('linkTypeId');
    if (!targetKey || !linkTypeId) return;
    try {
      const target = await pmApi.getIssueByKey(targetKey);
      if (!target?.issue) { setError(`Target issue not found: ${targetKey}`); return; }
      await jiraApi.addLink(issueId, { linkTypeId, targetIssueId: target.issue.id });
      e.currentTarget.reset(); setShowAdd(false); reload();
    } catch (err) { setError(err.response?.data?.message || 'Failed'); }
  }

  async function remove(linkId) {
    await jiraApi.removeLink(linkId); reload();
  }

  // Group by label so the section reads like JIRA: "blocks (2)", "is blocked by (1)", …
  const grouped = items.reduce((acc, l) => { (acc[l.label] = acc[l.label] || []).push(l); return acc; }, {});

  return (
    <>
      <div className="actions" style={{ marginBottom: 12 }}>
        <button className="btn primary small" onClick={() => setShowAdd((s) => !s)}>{showAdd ? 'Close' : '+ Link issue'}</button>
      </div>
      {showAdd && (
        <form onSubmit={add} className="card form-card grid-form">
          <label>Link type
            <select name="linkTypeId" required>
              <option value="">Select…</option>
              {linkTypes.map((t) => <option key={t.id} value={t.id}>{t.outward_name}</option>)}
            </select>
          </label>
          <label>Target issue (e.g. BOS-42)<input name="targetKey" required pattern="[A-Z][A-Z0-9]*-\d+" /></label>
          {error && <div className="alert error full">{error}</div>}
          <div className="full actions"><button className="btn primary" type="submit">Link</button></div>
        </form>
      )}
      {Object.keys(grouped).length === 0 && <div className="muted small">No links.</div>}
      {Object.entries(grouped).map(([label, list]) => (
        <div key={label} className="card">
          <h4>{label} ({list.length})</h4>
          <ul className="dep-list">
            {list.map((l) => (
              <li key={l.link_id}>
                <Link to={`/pm/issues/${l.project_key}-${l.issue_no}`}><code>{l.project_key}-{l.issue_no}</code> {l.title}</Link>
                <span className={`chip status-${l.status === 'done' ? 'approved' : 'pending'}`}>{l.status}</span>
                <button className="btn ghost small" onClick={() => remove(l.link_id)}>×</button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}
